import { type OrgUnitGuest, useOrgOrgUnitRetrieve } from "@innoclouds/api";
import { experimental_createQueryPersister } from "@tanstack/query-persist-client-core";
import { keepPreviousData } from "@tanstack/vue-query";
import { useLocalStorage } from "@vueuse/core";
import { defineStore } from "pinia";
import { computed, watch } from "vue";
import { useRoute } from "vue-router";
import { emitter } from "#/utils/emitter";

const persister = experimental_createQueryPersister({ storage: localStorage });

const ORG_UNIT_KEY = "orgID";

const getSubdomain = (url: string) => {
	if (!url?.trim()?.length) return null;

	const domain = url.includes("://") ? url.split("://")[1] : url;
	return domain.split(".")?.[0] || null;
};

export const useOrgUnitStore = defineStore("org-unit", () => {
	const route = useRoute();
	const fallback = computed(() => route.query.orgId?.toString() || route.params.orgId?.toString());
	const presistId = useLocalStorage<string>(ORG_UNIT_KEY, () => null);
	const _id = computed(() => fallback.value || presistId.value || (getSubdomain(window.location.host) as string));

	const {
		data,
		isLoading,
		isError,
		queryKey,
		refetch: refetchOrgUnit,
	} = useOrgOrgUnitRetrieve(_id, {
		query: {
			enabled: () => !!_id.value,
			// placeholderData: keepPreviousData,
			retry: false,
			staleTime: 300_000, // 5 minutes
			gcTime: 300_000, // 5 minutes
			refetchOnReconnect: true,
			refetchOnWindowFocus: false,
			refetchOnMount: false,
			// persister: persister.persisterFn,
		},
	});

	const refetch = () => refetchOrgUnit();

	// @TODO: Подумать о переносе в presist
	const onOrgUnitChange = () => {
		if (!fallback.value || !presistId.value || fallback.value === presistId.value) return;
		// @TODO: Точечно очищать чувствительные данные
		localStorage.removeItem("address");
		localStorage.removeItem("badgesCache");
		localStorage.removeItem("cart");
		localStorage.removeItem("default_language");
		localStorage.removeItem("themeHash");
		localStorage.removeItem("tableData");
		localStorage.removeItem("tableId");
		localStorage.removeItem("locale");

		sessionStorage.clear();
	};

	const presist = (orgUnit?: OrgUnitGuest) => {
		if (!orgUnit) return;
		presistId.value = orgUnit.id;
	};

	watch([fallback, presistId], onOrgUnitChange, { immediate: true });
	watch(data, presist, { immediate: true });

	return {
		id: computed(() => fallback.value || presistId.value),
		data,
		refetch,
		isLoading,
		isError,
		queryKey,
	};
});
